import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.webp";
import { LogOut } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

interface AppHeaderProps {
  rightContent?: React.ReactNode;
}

export default function AppHeader({ rightContent }: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  // Hide brand name on mobile for login page and counter/event pages
  const isLoginPage = location.pathname === "/app" && !user;
  const isEventPage = location.pathname.startsWith("/event") || location.pathname.startsWith("/counter");
  const hideBrandOnMobile = isLoginPage || isEventPage;

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        <button
          onClick={() => navigate(user ? "/app" : "/")}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img src={logo} alt="PartyFlow logo" className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg" />
          <span className={`font-display text-2xl font-bold tracking-tight text-[hsl(var(--color-yellow))] ${hideBrandOnMobile ? "hidden sm:inline" : ""}`}>
            PartyFlow.ch
          </span>
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher />
          {rightContent}
          {user ? (
            <Button onClick={signOut} variant="outline" size="sm" className="gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          ) : (
            <>
              <Button onClick={() => navigate("/app")} variant="outline" size="sm">
                Login
              </Button>
              <Button onClick={() => navigate("/app?mode=signup")} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">
                Sign up
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
